/**
 * @link https://shopify.dev/docs/api/admin-graphql/2024-10/objects/AppSubscription
 */

export const ShopifyAppSubscriptionStatus = [
  "ACTIVE",
  "CANCELLED",
  "DECLINED",
  "EXPIRED",
  "FROZEN",
  "PENDING",
  "ACCEPTED",
] as const;
export type ShopifyAppSubscriptionStatusType =
  (typeof ShopifyAppSubscriptionStatus)[number];

interface LineItem {
  id: string;
  plan: { pricingDetails: RecurringPlanPricing | UsagePlanPricing };
}

interface RecurringPlanPricing {
  __typename: "AppRecurringPricing";
  interval: string;
  price: { amount: string; currencyCode: string };
}

interface UsagePlanPricing {
  __typename: "AppUsagePricing";
  interval: string;
  terms: string;
  balanceUsed: { amount: string; currencyCode: string };
  cappedAmount: { amount: string; currencyCode: string };
}

export interface ShopifySubscription {
  id: string;
  status: ShopifyAppSubscriptionStatusType;
  createdAt: string;
  currentPeriodEnd: string;
  lineItems: LineItem[];
  trialDays?: number | null;
  name?: string | null;
}

export function getUsagePlanPricing(
  subscription: ShopifySubscription | undefined | null,
): UsagePlanPricing | undefined {
  return subscription?.lineItems
    .map((item) => item.plan.pricingDetails)
    .find((pricingDetails) => pricingDetails.__typename === "AppUsagePricing");
}

export function getRecurringPlanPricing(
  subscription: ShopifySubscription | undefined,
): RecurringPlanPricing | undefined {
  return subscription?.lineItems
    .map((item) => item.plan.pricingDetails)
    .find(
      (pricingDetails) => pricingDetails.__typename === "AppRecurringPricing",
    );
}

export function getUsagePlanSubscriptionId(
  subscription: ShopifySubscription | undefined,
): string | undefined {
  return subscription?.lineItems.find(
    (lineItem) => lineItem.plan.pricingDetails.__typename === "AppUsagePricing",
  )?.id;
}

/** Days to accept Shopify returns billing before the portal is disabled. */
export const SHOPIFY_RETURNS_BILLING_GRACE_DAYS = 14;

/** Mirrors ShopifyPaymentProvider.canBillTeam: no usage plan means not billable. */
export function hasAcceptedShopifyBilling(
  subscription: ShopifySubscription | undefined,
): boolean {
  return !!getUsagePlanSubscriptionId(subscription);
}

/** The +14d shutdown deadline, in UTC so server and client agree. */
export function getShopifyReturnsBillingDeadline(
  liveAt: Date | string,
): Temporal.Instant {
  const liveAtZdt = Temporal.Instant.from(
    typeof liveAt === "string" ? liveAt : liveAt.toISOString(),
  ).toZonedDateTimeISO("UTC");
  return liveAtZdt
    .add({ days: SHOPIFY_RETURNS_BILLING_GRACE_DAYS })
    .toInstant();
}
